import { useMemo } from 'react';
import SectionLabel from '../common/SectionLabel';
import { formatCurrency } from '../../utils/formatting';
import { useFinancialData } from '../../context/FinancialDataContext';
import { loadCashFlowActuals } from '../../utils/cashFlowLoader';

const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// 2025 actual distributions for projected month defaults
import overridesConfig from '../../config/overrides.json';
const DISTRIBUTIONS_2025 = overridesConfig.distributions2025 || new Array(12).fill(0);

function sumCat(monthData, category) {
  if (!monthData?.[category]) return 0;
  return Object.values(monthData[category]).reduce((s, v) => s + (v || 0), 0);
}

// Load user-edited CF overrides (investing CF, distributions) from localStorage
function loadCFOverrides() {
  try {
    const stored = localStorage.getItem('bcc_cf_overrides');
    if (stored) return JSON.parse(stored);
  } catch (e) { /* ignore */ }
  // Check for saved defaults
  try {
    const defaults = localStorage.getItem('bcc_cf_defaults');
    if (defaults) return JSON.parse(defaults);
  } catch (e) { /* ignore */ }
  return {};
}

export default function CashSnapshot({ monthlyImpact }) {
  const { global2026Monthly } = useFinancialData();
  const cashFlowData = useMemo(() => loadCashFlowActuals(), []);
  const threshold = cashFlowData.minimumThreshold;

  const analysis = useMemo(() => {
    // Build 12 months same way as CashFlow component
    let runningBalance = cashFlowData.startingBalance;
    const actualMonthCount = cashFlowData.months.length;
    const cfOverrides = loadCFOverrides();
    const balances = [];

    for (let i = 0; i < 12; i++) {
      const isActual = i < actualMonthCount;
      let operatingCF, investingCF, distributions, otherFinancing;

      if (isActual) {
        const ad = cashFlowData.months[i];
        operatingCF = ad.operatingCF;
        investingCF = ad.investingCF;
        distributions = ad.distributions;
        otherFinancing = ad.otherFinancing || 0;
      } else {
        const mk = MONTH_KEYS[i];
        const monthData = global2026Monthly?.months?.[mk];
        operatingCF = sumCat(monthData, 'income') - sumCat(monthData, 'cogs') - sumCat(monthData, 'expenses');
        const mo = cfOverrides[i] || {};
        investingCF = mo.investingCF ?? 0;
        distributions = mo.distributions ?? DISTRIBUTIONS_2025[i];
        otherFinancing = 0;
      }

      const scenarioDelta = monthlyImpact?.[i]?.net || 0;
      const beginBalance = i === 0 ? cashFlowData.startingBalance : runningBalance;
      const netChange = operatingCF + investingCF + distributions + otherFinancing + scenarioDelta;
      const endingBalance = beginBalance + netChange;

      balances.push({ endingBalance, netChange, label: MONTH_LABELS[i], isWatch: endingBalance < threshold });
      runningBalance = endingBalance;
    }

    const lastActualIdx = actualMonthCount - 1;
    const currentBalance = balances[lastActualIdx]?.endingBalance || 0;
    const currentMonth = MONTH_LABELS[lastActualIdx];
    const yearEndBalance = balances[11]?.endingBalance || 0;
    const lowestRow = balances.reduce((min, r) => r.endingBalance < min.endingBalance ? r : min, balances[0]);
    const watchCount = balances.filter(r => r.isWatch).length;

    const projectedRows = balances.slice(actualMonthCount);
    const avgMonthlyChange = projectedRows.length > 0
      ? projectedRows.reduce((s, r) => s + r.netChange, 0) / projectedRows.length : 0;

    return {
      currentBalance, currentMonth, yearEndBalance,
      lowestBalance: lowestRow.endingBalance, lowestMonth: lowestRow.label,
      watchCount, threshold, avgMonthlyChange,
    };
  }, [global2026Monthly, monthlyImpact, cashFlowData]);

  const isHealthy = analysis.watchCount === 0;
  const lowestIsWatch = analysis.lowestBalance < analysis.threshold;

  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
      <SectionLabel>Cash Position</SectionLabel>

      <div className="mt-2 mb-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#999] mb-0.5">
          Balance as of {analysis.currentMonth}
        </div>
        <div className={`text-[28px] font-bold ${analysis.currentBalance >= analysis.threshold ? 'text-navy' : 'text-rose'}`}>
          {formatCurrency(analysis.currentBalance)}
        </div>
      </div>

      <div className="space-y-2.5 text-[12px]">
        <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
          <span className="text-[#888]">Year-End Projected</span>
          <span className={`font-bold ${analysis.yearEndBalance >= analysis.threshold ? 'text-deep-teal' : 'text-rose'}`}>
            {formatCurrency(analysis.yearEndBalance)}
          </span>
        </div>
        <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
          <span className="text-[#888]">Lowest Point</span>
          <div className="text-right">
            <span className={`font-bold ${lowestIsWatch ? 'text-rose' : 'text-navy'}`}>
              {formatCurrency(analysis.lowestBalance)}
            </span>
            <span className="text-[10px] text-[#aaa] ml-1">({analysis.lowestMonth})</span>
          </div>
        </div>
        <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
          <span className="text-[#888]">Min. Threshold</span>
          <span className="font-bold text-navy">{formatCurrency(analysis.threshold)}</span>
        </div>
        <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
          <span className="text-[#888]">Avg Monthly Net CF</span>
          <span className={`font-bold ${analysis.avgMonthlyChange >= 0 ? 'text-deep-teal' : 'text-rose'}`}>
            {analysis.avgMonthlyChange >= 0 ? '+' : ''}{formatCurrency(analysis.avgMonthlyChange)}
          </span>
        </div>
        <div className="flex justify-between items-center py-2 border-t border-gray-100">
          <span className="text-[#888]">Months at Risk</span>
          {isHealthy ? (
            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-[3px] bg-deep-teal/10 text-deep-teal">None</span>
          ) : (
            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-[3px] bg-rose/10 text-rose">
              {analysis.watchCount} month{analysis.watchCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
